import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Euro as EuroIcon, CurrencyExchange as CurrencyIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { fetchCurrencySettings, updateCurrencySettings } from '../../store/slices/settingsSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useDispatch();
  const { currencySettings, loading: settingsLoading, error: settingsError } = useSelector((state: RootState) => state.settings);

  const generalFormik = useFormik({
    initialValues: {
      siteName: 'Golden Books',
      siteDescription: 'Your trusted online bookstore',
      contactEmail: 'contact@goldenbooks.com',
      supportEmail: 'support@goldenbooks.com',
      maintenanceMode: false,
      allowRegistration: true,
    },
    validationSchema: Yup.object({
      siteName: Yup.string().required('Site name is required'),
      siteDescription: Yup.string().required('Site description is required'),
      contactEmail: Yup.string().email('Invalid email').required('Contact email is required'),
      supportEmail: Yup.string().email('Invalid email').required('Support email is required'),
    }),
    onSubmit: async (values) => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success('Settings updated successfully');
      } catch (error) {
        toast.error('Failed to update settings');
      }
    },
  });

  const emailFormik = useFormik({
    initialValues: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: '587',
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: 'noreply@goldenbooks.com',
      fromName: 'Golden Books',
    },
    validationSchema: Yup.object({
      smtpHost: Yup.string().required('SMTP host is required'),
      smtpPort: Yup.number().required('SMTP port is required'),
      smtpUsername: Yup.string().required('SMTP username is required'),
      smtpPassword: Yup.string().required('SMTP password is required'),
      fromEmail: Yup.string().email('Invalid email').required('From email is required'),
      fromName: Yup.string().required('From name is required'),
    }),
    onSubmit: async (values) => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success('Email settings updated successfully');
      } catch (error) {
        toast.error('Failed to update email settings');
      }
    },
  });

  const currencyFormik = useFormik({
    initialValues: {
      currency_display_mode: currencySettings.currency_display_mode,
      bgn_to_eur_rate: currencySettings.bgn_to_eur_rate,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      currency_display_mode: Yup.string().required('Currency display mode is required'),
      bgn_to_eur_rate: Yup.number()
        .required('Exchange rate is required')
        .positive('Exchange rate must be positive')
        .min(0.01, 'Exchange rate must be at least 0.01')
        .max(10, 'Exchange rate must not exceed 10'),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(updateCurrencySettings(values) as any);
        toast.success('Currency settings updated successfully');
      } catch (error) {
        toast.error('Failed to update currency settings');
      }
    },
  });

  useEffect(() => {
    dispatch(fetchCurrencySettings() as any);
  }, [dispatch]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
        System Settings
      </Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="General" />
            <Tab label="Email Configuration" />
            <Tab label="Security" />
            <Tab label="Currency" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            General Settings
          </Typography>
          <form onSubmit={generalFormik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="siteName"
                  label="Site Name"
                  value={generalFormik.values.siteName}
                  onChange={generalFormik.handleChange}
                  onBlur={generalFormik.handleBlur}
                  error={generalFormik.touched.siteName && Boolean(generalFormik.errors.siteName)}
                  helperText={generalFormik.touched.siteName && generalFormik.errors.siteName}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="siteDescription"
                  label="Site Description"
                  value={generalFormik.values.siteDescription}
                  onChange={generalFormik.handleChange}
                  onBlur={generalFormik.handleBlur}
                  error={generalFormik.touched.siteDescription && Boolean(generalFormik.errors.siteDescription)}
                  helperText={generalFormik.touched.siteDescription && generalFormik.errors.siteDescription}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="contactEmail"
                  label="Contact Email"
                  type="email"
                  value={generalFormik.values.contactEmail}
                  onChange={generalFormik.handleChange}
                  onBlur={generalFormik.handleBlur}
                  error={generalFormik.touched.contactEmail && Boolean(generalFormik.errors.contactEmail)}
                  helperText={generalFormik.touched.contactEmail && generalFormik.errors.contactEmail}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="supportEmail"
                  label="Support Email"
                  type="email"
                  value={generalFormik.values.supportEmail}
                  onChange={generalFormik.handleChange}
                  onBlur={generalFormik.handleBlur}
                  error={generalFormik.touched.supportEmail && Boolean(generalFormik.errors.supportEmail)}
                  helperText={generalFormik.touched.supportEmail && generalFormik.errors.supportEmail}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Site Options
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={generalFormik.values.maintenanceMode}
                      onChange={(e) => generalFormik.setFieldValue('maintenanceMode', e.target.checked)}
                      name="maintenanceMode"
                    />
                  }
                  label="Maintenance Mode"
                />
                <Typography variant="caption" display="block" color="textSecondary">
                  When enabled, only admins can access the site
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={generalFormik.values.allowRegistration}
                      onChange={(e) => generalFormik.setFieldValue('allowRegistration', e.target.checked)}
                      name="allowRegistration"
                    />
                  }
                  label="Allow User Registration"
                />
                <Typography variant="caption" display="block" color="textSecondary">
                  Allow new users to register accounts
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!generalFormik.isValid}
                >
                  Save General Settings
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Email Configuration
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Configure SMTP settings for sending emails
          </Typography>
          <form onSubmit={emailFormik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="smtpHost"
                  label="SMTP Host"
                  value={emailFormik.values.smtpHost}
                  onChange={emailFormik.handleChange}
                  onBlur={emailFormik.handleBlur}
                  error={emailFormik.touched.smtpHost && Boolean(emailFormik.errors.smtpHost)}
                  helperText={emailFormik.touched.smtpHost && emailFormik.errors.smtpHost}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="smtpPort"
                  label="SMTP Port"
                  type="number"
                  value={emailFormik.values.smtpPort}
                  onChange={emailFormik.handleChange}
                  onBlur={emailFormik.handleBlur}
                  error={emailFormik.touched.smtpPort && Boolean(emailFormik.errors.smtpPort)}
                  helperText={emailFormik.touched.smtpPort && emailFormik.errors.smtpPort}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="smtpUsername"
                  label="SMTP Username"
                  value={emailFormik.values.smtpUsername}
                  onChange={emailFormik.handleChange}
                  onBlur={emailFormik.handleBlur}
                  error={emailFormik.touched.smtpUsername && Boolean(emailFormik.errors.smtpUsername)}
                  helperText={emailFormik.touched.smtpUsername && emailFormik.errors.smtpUsername}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="smtpPassword"
                  label="SMTP Password"
                  type="password"
                  value={emailFormik.values.smtpPassword}
                  onChange={emailFormik.handleChange}
                  onBlur={emailFormik.handleBlur}
                  error={emailFormik.touched.smtpPassword && Boolean(emailFormik.errors.smtpPassword)}
                  helperText={emailFormik.touched.smtpPassword && emailFormik.errors.smtpPassword}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="fromEmail"
                  label="From Email"
                  type="email"
                  value={emailFormik.values.fromEmail}
                  onChange={emailFormik.handleChange}
                  onBlur={emailFormik.handleBlur}
                  error={emailFormik.touched.fromEmail && Boolean(emailFormik.errors.fromEmail)}
                  helperText={emailFormik.touched.fromEmail && emailFormik.errors.fromEmail}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="fromName"
                  label="From Name"
                  value={emailFormik.values.fromName}
                  onChange={emailFormik.handleChange}
                  onBlur={emailFormik.handleBlur}
                  error={emailFormik.touched.fromName && Boolean(emailFormik.errors.fromName)}
                  helperText={emailFormik.touched.fromName && emailFormik.errors.fromName}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!emailFormik.isValid}
                >
                  Save Email Settings
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Configure security-related settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Password Requirements
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • Minimum 8 characters<br />
                • At least one uppercase letter<br />
                • At least one lowercase letter<br />
                • At least one number
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Session Settings
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • Session timeout: 24 hours<br />
                • Remember me duration: 30 days
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Currency Settings
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Configure how prices are displayed throughout the site
          </Typography>

          {settingsError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {settingsError}
            </Alert>
          )}

          {settingsLoading && (
            <Box display="flex" justifyContent="center" mb={3}>
              <CircularProgress />
            </Box>
          )}

          <form onSubmit={currencyFormik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="currency-display-mode-label">Currency Display Mode</InputLabel>
                  <Select
                    labelId="currency-display-mode-label"
                    name="currency_display_mode"
                    value={currencyFormik.values.currency_display_mode}
                    onChange={currencyFormik.handleChange}
                    onBlur={currencyFormik.handleBlur}
                    error={currencyFormik.touched.currency_display_mode && Boolean(currencyFormik.errors.currency_display_mode)}
                    label="Currency Display Mode"
                  >
                    <MenuItem value="dual">Dual Currency (BGN / EUR)</MenuItem>
                    <MenuItem value="eur_only">EUR Only</MenuItem>
                  </Select>
                  {currencyFormik.touched.currency_display_mode && currencyFormik.errors.currency_display_mode && (
                    <Typography variant="caption" color="error">
                      {currencyFormik.errors.currency_display_mode}
                    </Typography>
                  )}
                </FormControl>
                <Typography variant="caption" display="block" color="textSecondary" mt={1}>
                  Choose how prices are displayed to customers
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="bgn_to_eur_rate"
                  label="BGN to EUR Exchange Rate"
                  type="number"
                  inputProps={{ step: 0.00001, min: 0.01, max: 10 }}
                  value={currencyFormik.values.bgn_to_eur_rate}
                  onChange={currencyFormik.handleChange}
                  onBlur={currencyFormik.handleBlur}
                  error={currencyFormik.touched.bgn_to_eur_rate && Boolean(currencyFormik.errors.bgn_to_eur_rate)}
                  helperText={currencyFormik.touched.bgn_to_eur_rate && currencyFormik.errors.bgn_to_eur_rate}
                  required
                />
                <Typography variant="caption" display="block" color="textSecondary">
                  Current official rate: 1 EUR = 1.95583 BGN
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {currencyFormik.values.currency_display_mode === 'dual' ? (
                    <Typography variant="body2">
                      Sample price: 45.00 лв. / {(45 / currencyFormik.values.bgn_to_eur_rate).toFixed(2)} €
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      Sample price: {(45 / currencyFormik.values.bgn_to_eur_rate).toFixed(2)} €
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<CurrencyIcon />}
                  disabled={!currencyFormik.isValid || settingsLoading}
                >
                  Save Currency Settings
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default Settings;
